// Updated ConfigSection.js with custom popup modal for add/edit/delete

import React, { useEffect, useState } from "react";
import { GrAddCircle } from "react-icons/gr";
import { CiEdit } from "react-icons/ci";
import { RiDeleteBinLine } from "react-icons/ri";
import config from "../../config";
import ActionPopup from "../../components/ActionPopup"; // Adjust path as needed

const ConfigSection = ({
  title,
  configType,
  userDetails,
  selectedClient,
  configKey,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [popupConfig, setPopupConfig] = useState(null);

  const fetchConfigs = async () => {
    if (!userDetails.token || !userDetails.orgId || !selectedClient) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${userDetails.orgId}?configType=${configType}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      const activeItems = data.filter((item) => item.isActive);
      setItems(activeItems || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, [userDetails.token, userDetails.orgId, selectedClient]);

  const saveData = async (data, action) => {
    try {
      const response = await fetch(
        `${config.API_BASE_URL}${config.ADD_CLIENT_CONFIG_ENDPOINT}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );
      if (!response.ok) throw new Error("Request failed");
      fetchConfigs();
    } catch (err) {
      console.error(`${action} error:`, err);
    }
  };

  const handleAction = (action, index = null) => {
    if (action === "add") {
      setPopupConfig({
        type: "add",
        title,
        defaultValue: "",
        onConfirm: (inputValue) => {
          const data = {
            orgId: userDetails.orgId,
            values: inputValue,
            isActive: true,
            [configKey]: configType,
          };
          saveData(data, action);
          setPopupConfig(null);
        },
        onCancel: () => setPopupConfig(null),
      });
    } else if (action === "edit" && index !== null) {
      setPopupConfig({
        type: "edit",
        title,
        defaultValue: items[index].values,
        onConfirm: (updatedName) => {
          const data = {
            ...items[index],
            values: updatedName,
            isActive: true,
            [configKey]: configType,
          };
          saveData(data, action);
          setPopupConfig(null);
        },
        onCancel: () => setPopupConfig(null),
      });
    } else if (action === "delete" && index !== null) {
      setPopupConfig({
        type: "delete",
        title,
        defaultValue: items[index].values,
        onConfirm: () => {
          const data = {
            ...items[index],
            isActive: false,
            [configKey]: configType,
          };
          saveData(data, action);
          setPopupConfig(null);
        },
        onCancel: () => setPopupConfig(null),
      });
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className='grid-item'>
      <h2 className='section-title'>
        {title}
        <button
          className='add-config-button'
          onClick={() => handleAction("add")}
        >
          <GrAddCircle /> <span>Add New</span>
        </button>
      </h2>
      <div className='config-table-wrapper'>
        <table className='config-table'>
          <thead>
            <tr>
              <th>{title}</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={item.id || index}>
                <td>{item.values}</td>
                <td className='actions'>
                  <button onClick={() => handleAction("edit", index)}>
                    <CiEdit />
                  </button>
                  <button onClick={() => handleAction("delete", index)}>
                    <RiDeleteBinLine />
                  </button>
                </td>
              </tr>
            ))}
          
            {/* Pad with empty rows to maintain a minimum of 3 static rows for visual consistency */}
            {Array.from({ length: Math.max(0, 3 - items.length) }).map((_, i) => (
              <tr key={`empty-${i}`} className="empty-static-row">
                <td style={{ height: '12px' }}>&nbsp;</td>
                <td>&nbsp;</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {popupConfig && (
        <ActionPopup
          type={popupConfig.type}
          title={popupConfig.title}
          defaultValue={popupConfig.defaultValue}
          onConfirm={popupConfig.onConfirm}
          onCancel={popupConfig.onCancel}
          inputType={title === "Shift Timing" ? "timeRange" : "text"}
        />
      )}
    </div>
  );
};

export default ConfigSection;
